import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Icons
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

// Custom components
import GridContainer from '../components/common/GridContainer';
import GridItem from '../components/common/GridItem';

const NotFoundPage: React.FC = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 12,
        background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
          zIndex: 0,
        }}
      />
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: 12 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 3,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h1" 
                component="h1" 
                sx={{ 
                  fontSize: { xs: '6rem', md: '8rem' },
                  fontWeight: 700,
                  color: 'primary.main',
                  mb: 2
                }}
              >
                404
              </Typography>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom
                sx={{ 
                  fontWeight: 500,
                  mb: 2
                }}
              >
                Page Not Found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
                We're sorry, but the page you are looking for does not exist or has been moved.
                Please check the URL or use the navigation options below.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </Box>
            
            <Typography variant="h6" gutterBottom>
              Popular Destinations
            </Typography>
            
            <GridContainer spacing={2} sx={{ maxWidth: 600, mx: 'auto', mb: 6 }}>
              <GridItem xs={12} sm={6}>
                <Button
                  component={RouterLink}
                  to="/"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  startIcon={<HomeIcon />}
                  sx={{ py: 1.5 }}
                >
                  Back to Home
                </Button>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <Button
                  component={RouterLink}
                  to="/fleet"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  size="large"
                  startIcon={<DirectionsCarIcon />}
                  sx={{ py: 1.5 }}
                >
                  View Our Fleet
                </Button>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <Button
                  component={RouterLink}
                  to="/booking"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  size="large"
                  sx={{ py: 1.5 }}
                >
                  Book Now
                </Button>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  size="large"
                  startIcon={<ContactSupportIcon />}
                  sx={{ py: 1.5 }}
                >
                  Contact Us
                </Button>
              </GridItem>
            </GridContainer>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                If you believe this is an error, please contact our support team.
              </Typography>
              <Button
                size="small"
                color="primary"
                component={RouterLink}
                to="/contact"
                sx={{ textTransform: 'none' }}
              >
                Report this issue
              </Button>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default NotFoundPage; 